import type { AuditCenterRow, AuditRecordKind } from '@/types/audit'

/**
 * Shared words for the audit trail layouts (lib/auditViews): readable values,
 * field changes from → to, page and subject names, record nouns. Never ids.
 *
 * Pure, so the rules are unit-tested (vitest here runs `.ts` only).
 */

export type ChangeTone = 'create' | 'update' | 'remove' | 'complete' | 'access' | 'stock'

export interface FieldChange {
  label: string
  /** Absent when the value was set for the first time (a creation). */
  from?: string
  to: string
}

/** The pages of the app, by their first path segment, for "Opened …". */
const PAGE_NAMES: Record<string, string> = {
  dashboard: 'Home',
  dashboards: 'Dashboards',
  'shift-dashboard': 'Shift Dashboard',
  procedures: 'Procedures',
  capa: 'CAPA',
  kits: 'Kits',
  mes: 'MES',
  board: 'Overall Status',
  'my-steps': 'My Steps',
  patients: 'Patients',
  forms: 'Forms',
  submissions: 'Submissions',
  'form-definitions': 'Form Library',
  'form-builder': 'Form Builder',
  'procedure-templates': 'Procedure Templates',
  inventory: 'Inventory',
  cleaning: 'Cleaning',
  equipment: 'Equipment',
  products: 'Products',
  resources: 'Resources',
  audit: 'Audit Trail Center',
  reports: 'Reports',
  notifications: 'Notifications',
  configure: 'Configure',
  integrations: 'Integrations',
  settings: 'Settings',
  profile: 'Profile',
  wiki: 'Help',
  m: 'Mobile',
}

/** "/inventory" → "Inventory", "/patients/MRN-1" → "Patients · MRN-1", unknown → the path. */
export function pageName(path: string): string {
  const [first, ...rest] = path.split('/').filter(Boolean)
  if (!first) return 'Home'
  const name = PAGE_NAMES[first]
  if (!name) return path
  // A mobile screen: "Mobile · Tasks".
  if (first === 'm') return rest.length ? `Mobile · ${humanizeKey(rest[0])}` : 'Mobile'
  return rest.length ? `${name} · ${decodeURIComponent(rest[0])}` : name
}

/** The parts of a row a value comparison reads. */
type Row = Pick<AuditCenterRow, 'itemChanged' | 'oldValue' | 'newValue'>

/** Keys no reader asks about: ids, bookkeeping, and the tenant. */
function noiseKey(key: string): boolean {
  return key === 'id' || key === 'tenantId' || key === 'createdAt' || key === 'updatedAt' || /Id$/.test(key) || /Ids$/.test(key)
}

/** `dueDate` → "Due date", `volumeMl` → "Volume ml", `statusNote` → "Status note". */
export function humanizeKey(key: string): string {
  const words = key
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .trim()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/
const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/

/** One value, short and readable: dates as dates, yes/no, text as is. */
export function readableValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'number') return String(value)
  if (typeof value === 'string') {
    if (ISO_DAY.test(value)) {
      const [y, m, d] = value.split('-').map(Number)
      return new Date(y, m - 1, d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    }
    if (ISO_INSTANT.test(value)) {
      const at = new Date(value)
      if (!Number.isNaN(at.getTime())) {
        return at.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
      }
    }
    return value.replace(/_/g, ' ')
  }
  if (Array.isArray(value)) return value.length ? value.map(readableValue).join(', ') : '—'
  const text = JSON.stringify(value)
  return text.length > 80 ? `${text.slice(0, 77)}…` : text
}

function asObject(v: unknown): Record<string, unknown> | null {
  return typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Record<string, unknown>) : null
}

/**
 * The values that changed, from → to. An object payload is compared key by
 * key; ids and bookkeeping are left out. A creation lists what was set.
 */
export function changesOf(row: Row): FieldChange[] {
  const before = asObject(row.oldValue)
  const after = asObject(row.newValue)
  if (before || after) {
    const keys = [...new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})])].filter(k => !noiseKey(k))
    const changed = keys.filter(k => JSON.stringify(before?.[k] ?? null) !== JSON.stringify(after?.[k] ?? null))
    if (!before && after) {
      // Created: what it was set to, the first few fields.
      return changed.slice(0, 5).map(k => ({ label: humanizeKey(k), to: readableValue(after[k]) }))
    }
    if (before && !after) return []
    return changed.slice(0, 6).map(k => ({ label: humanizeKey(k), from: readableValue(before?.[k]), to: readableValue(after?.[k]) }))
  }
  if (row.oldValue == null && row.newValue == null) return []
  const label = row.itemChanged?.trim() || 'Value'
  if (row.oldValue == null || row.oldValue === '') return [{ label, to: readableValue(row.newValue) }]
  return [{ label, from: readableValue(row.oldValue), to: readableValue(row.newValue) }]
}

/** What kind of record a row is about, in words. */
export const RECORD_NOUN: Record<AuditRecordKind, string> = {
  patient: 'Patient',
  product: 'Product',
  storage: 'Storage position',
  equipment: 'Equipment',
  submission: 'Form',
  lot: 'Inventory lot',
  material: 'Material',
  pick_list: 'Pick list',
  transfer: 'Storage transfer',
  zone: 'Storage zone',
  profile: 'Inventory profile',
  user_group: 'Email group',
  procedure: 'Procedure',
  user: 'User',
  role: 'Role',
  equipment_type: 'Equipment type',
  capability: 'Qualification',
  instance: 'Instance',
  library_field: 'Library field',
  form_definition: 'Form definition',
  procedure_template: 'Procedure template',
  step_setup: 'Process step setup',
  integration: 'Integration',
  action_item: 'Action item',
  mes_product: 'Product code',
}

const SUBJECT_TYPE: Record<string, string> = { mrn: 'MRN', kit: 'Kit', wo: 'Work order', mfg: 'MFG' }

/**
 * What a subject is. A kit run keeps its kit id where a patient procedure keeps
 * its MRN, so a stored "mrn" type is corrected by the id's own prefix.
 */
export function subjectLabel(value: string, type?: string | null): string {
  if (/^KIT-/i.test(value)) return 'Kit'
  if (/^WO-/i.test(value)) return 'Work order'
  if (/^MFG-/i.test(value)) return 'MFG'
  return SUBJECT_TYPE[type ?? ''] ?? 'Subject'
}

/** Break a path after each "/" rather than inside a name: "LN2-01/Vapor/R4". */
export function breakablePath(text: string): string {
  return text.replace(/\//g, '/\u200b')
}
